import React, { useEffect, useState } from 'react';
import Moment from "moment/moment";
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/main/rootReducer';
import { PurchasedItemsTable } from './PurchasedItemsTable';
import { getComparator, stableSort } from './SortTableHelper';
import CancelRoundedIcon from "@material-ui/icons/CancelRounded";
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';
import { AppendDollarToAmount, ConvertAmountToDollar, GetPurchasedItems, 
    getVenuetDateTimeFromUtc, IsSearchedTextFound, MakeBookingDetails } from './helper';
import { GenerateCSV, SanitizeValueForCsv } from './CSVGeneratorHelper';
import { IPurchasedItem } from 'app/services/reporting/reporting.type';
import { Order, IBookingDetails, IBookingDetailsTableHeader, IFilterParameters, IGuestsReportTableHeader, IBookingDetailsPageProps } from './types';
import { Button, createStyles, Grid, IconButton, InputAdornment, makeStyles, 
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, 
  TablePagination, TableRow, TableSortLabel, TextField, Theme } from '@material-ui/core';
import { ArrowBackIos, ArrowForwardIos, 
  KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@material-ui/icons';
import { StoreBookingsInfoByFilters } from './service';
import { saveFromDate, saveSearchText, saveToDate, saveToDateDisplay, setExportBtnClicked } from 'app/reducers/dashboardSlice'
import { ReportingService } from 'app/services/reporting/reporting.service';

const NS = 'BookingDetailsPage';

/** Styles for booking details page */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
  root: {
    '& .MuiTableCell-head': {
      fontWeight: 'bold'
    },
    '& .MuiTablePagination-toolbar': {
      minHeight: '48px'
    },
    '& .MuiTableCell-root': {
      padding: '12px 2px'
    },
    marginTop: '10px'
  },
  darkBlueColour: {
    color: 'darkblue'
  },
  crimsonColour: {
    color: 'crimson',
    fontSize: '16px'
  },
  darkGreenColour: {
    fontSize: '16px',
    color: 'darkgreen'
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  buttonGroupRoot: {
    flexGrow: 1,
    justifyContent: 'space-between',
    '& .MuiOutlinedInput-input': {
      padding: '14.5px 14px'
    }
  },
  nextPrevWeekButton: {
    margin: theme.spacing(2)
  },
  searchTextField: {
    width: '250px',
    margin: '16px 0px 0px 16px',
    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: '0px'
    }
  }
}));

const headerCells: IBookingDetailsTableHeader[] = [
  { id: 'lastName', label: 'Surname', includeInCsv: true },
  { id: 'firstName', label: 'Firstname', includeInCsv: true },
  { id: 'companyName', label: 'Company', includeInCsv: true },
  { id: 'venueName', label: 'Venue', includeInCsv: true },
  { id: 'bookingStatus', label: 'Status', includeInCsv: true },
  { id: 'totalAmountInDollar', label: 'Spend Total (Sum)', includeInCsv: true, minWidth: 90 },
  { id: 'averageAmountInDollar', label: 'Spend Average PH', minWidth: 120 },
  { id: 'averageAmountSpentOnFoodInDollar', label: 'Food Average Spend', minWidth: 120 },
  { id: 'averageAmountSpentOnBeverageInDollar', label: 'Beverage Average Spend', minWidth: 120 },
  { id: 'bookingDuration', label: 'Duration' },
  { id: 'bookingTime', label: 'Date Time', minWidth: 150 },
  { id: 'tableList', label: 'Table' },
  { id: 'tags', label: 'Tags' },
  { id: 'customerVisitCount', label: 'Visits' }
];

const downloadReportHeaderCells: IGuestsReportTableHeader[] = [
  { id: 'lastName', label: 'Surname' },
  { id: 'firstName', label: 'Firstname' },
  { id: 'companyName', label: 'Company' },
  { id: 'venueName', label: 'Venue' },
  { id: 'bookingStatus', label: 'Status' },
  { id: 'totalAmountInDollar', label: 'Spend Total (Sum)' },
  { id: 'averageAmountInDollar', label: 'Spend Average PH' },
  { id: 'averageAmountSpentOnFoodInDollar', label: 'Food Average Spend' },
  { id: 'averageAmountSpentOnBeverageInDollar', label: 'Beverage Average Spend' },
  { id: 'bookingDuration', label: 'Duration' },
  { id: 'bookingTime', label: 'Date Time' },
  { id: 'tableList', label: 'Table' },
  { id: 'tags', label: 'Tags' },
  { id: 'customerVisitCount', label: 'Visits' },
  { id: 'transactionDateTime', label: 'Date' },
  { id: 'sku', label: 'SKU' },
  { id: 'productName', label: 'Item' },
  { id: 'description', label: 'Description' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'unitPrice', label: 'Price' },
  { id: 'salesCategoryName', label: 'Category' },
] 

/**
 * Displays the list of booking details in tabular format
 */
export const BookingDetailsPage = (props: IBookingDetailsPageProps) => {

  const rowsPerPage = 10;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [page, setPage] = useState<number>(0);
  const [appliedFilterParameters, setAppliedFilterParameters] = useState<IFilterParameters>(null);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IBookingDetails>('lastName');
  const [rows, setTableRows] = useState<IBookingDetails[]>([]);
  const { filteredBookingList } = useSelector((state: RootState) => state.venueReducer);
  const { filterParameters } = useSelector((state: RootState) => state.dashboardReducer);

  useEffect(() => {

      let dataRows = filteredBookingList.map(booking => MakeBookingDetails(booking));

      // Search text length must be greater than 2 before searching.
      if ((filterParameters.searchText?.length ?? 0) > 2) {
        dataRows = dataRows.filter(booking => IsSearchedTextFound(booking, filterParameters.searchText));
      }
      setTableRows(dataRows);
      // Navigate to the first page, whenever data is refreshed.
      setPage(0);
  }, [filteredBookingList, filterParameters.searchText]);

   useEffect(() => {

        try { 
            if (filterParameters.exportBtnClicked === true) {
                GenerateBookingDetailsCsv();
            }

        } catch (e) {
            console.log("Exception exporting csv" + e);
        }
        finally { 
            dispatch(setExportBtnClicked(false)); 
        }

   }, [filterParameters.exportBtnClicked]);

  useEffect(() => { 
      setAppliedFilterParameters(filterParameters);
  }, [filterParameters.isLoading]);

  
  function GenerateBookingDetailsCsv() { 

    props.onHandleLoadSpinner(true);
    ReportingService.getReportsDataForDownload(appliedFilterParameters, true)
      .then((result) => {
        let lines = [];

        // push headers
        let headers = downloadReportHeaderCells.map(item => item.label).join(',');
        lines.push(headers);

        for (const guestItem of result) {

          const { lastName,
            firstName,
            company,
            venueName,
            duration,
            time,
            tableList,
            visits, 
            transactionDateTime,
            sku,
            productName,
            description,
            quantity,
            unitPriceInCent,
            salesCategoryName
          } = guestItem;
          const bookingStatus = guestItem.status?.name ?? "";
          const unitPriceInDollar = ConvertAmountToDollar(unitPriceInCent);
          const totalAmountInDollar = ConvertAmountToDollar(guestItem.customer?.totalSpend);
          const averageAmountInDollar = ConvertAmountToDollar(guestItem.customer?.averageSpend);
          const averageAmountSpentOnFoodInDollar = ConvertAmountToDollar(guestItem.customer?.averageFoodSpend);
          const averageAmountSpentOnBeverageInDollar = ConvertAmountToDollar(guestItem.customer?.averageBeverageSpend);

          const tags = guestItem.tags.map(item => item.name).toString() || "";
          
          const lineItem = [ lastName, 
            firstName, company, venueName, bookingStatus, `$${totalAmountInDollar.toFixed(2)}`, 
            `$${averageAmountInDollar.toFixed(2)}`, `$${averageAmountSpentOnFoodInDollar.toFixed(2)}`,
            `$${averageAmountSpentOnBeverageInDollar.toFixed(2)}`, `${duration ? duration : ""}`, time, tableList, tags, 
            `${visits}`, transactionDateTime, sku, productName, description, `${quantity ? quantity : ""}`, `$${unitPriceInDollar.toFixed(2)}`,
            salesCategoryName
          ];
           lines.push(lineItem.map(m => SanitizeValueForCsv(m)).join(','))
        }

        let csvString = lines.join("\n");
        const { selectedReport, selectedVenue, fromDate, toDate } = appliedFilterParameters;
        GenerateCSV(`${selectedReport.name}_${selectedVenue.name}_${fromDate}_${toDate}`, csvString);
      })
      .finally(() => props.onHandleLoadSpinner(false));
  }

  /** Sort the column in asc/desc order */
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IBookingDetails) => {
    /**
     * isAsc is false, when a new column is selected for sorting in ascending order.
     * isAsc is true, when the same column is selected again for sorting but, this time in descending order.
     */
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const onNextPrevWeekButtonClickHandler = (offset: number) => {
    let fromDateParam = new Date(filterParameters.fromDate);
    let toDateParam = new Date(filterParameters.toDate);
    let toDateDisplayParam = new Date(filterParameters.toDateDisplay);
  
    if (offset < 0) {
      toDateParam = new Date(filterParameters.fromDate);
      toDateParam.setDate(new Date(filterParameters.fromDate).getDate() + 1);
      toDateDisplayParam = new Date(filterParameters.fromDate);
      fromDateParam.setDate(fromDateParam.getDate() - 7);
    } else {
      fromDateParam = new Date(filterParameters.toDateDisplay); // because the toDate Display is the correct value.
      toDateParam.setDate(fromDateParam.getDate() + 8); // because for filter range we want start of next day
      toDateDisplayParam.setDate(fromDateParam.getDate() + 7); // because for display we still show the range
    }

    const fromDateParamString = Moment(fromDateParam).format('YYYY-MM-DD');
    const toDateParamString = Moment(toDateParam).format('YYYY-MM-DD');
    const toDateDisplayParamString = Moment(toDateDisplayParam).format('YYYY-MM-DD');
    dispatch(saveFromDate(fromDateParamString));
    dispatch(saveToDate(toDateParamString));
    dispatch(saveToDateDisplay(toDateDisplayParamString));
    
    StoreBookingsInfoByFilters({ 
      ...filterParameters, 
      fromDate: fromDateParamString, 
      toDate: toDateParamString, 
      toDateDisplay: toDateDisplayParamString 
    });
  }

  const Row = (booking: IBookingDetails) => {

    const [open, setOpen] = React.useState(false);
    const [itemList, setPurchasedItemList] = useState<IPurchasedItem[]>([]);

    /**
     * Display purchased items in the table
     * Expand/Collapse the purchased items table
     */
    const togglePurchasedItemsTable = async (venueId: number, customerId: string) => {

      // Fetch customer's purchased items when table is expanded
      if (!open) {
          const productList = await GetPurchasedItems(venueId, customerId).then(items => {
              items.forEach(function (item) {
                  item.transactionDateTime = getVenuetDateTimeFromUtc(filterParameters.selectedVenue.timeZoneId, item.transactionDateTime)
              });
              return items;
          })
        setPurchasedItemList(productList);
      }

      setOpen(!open);
    }

    return (
      <>
        <TableRow hover onClick={() => togglePurchasedItemsTable(filterParameters.selectedVenue.id, booking.customerId)}>
          <TableCell>
            <IconButton aria-label="expand row" size="small">
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell>{booking.lastName}</TableCell>
          <TableCell>{booking.firstName}</TableCell>
          <TableCell>{booking.companyName}</TableCell>
          <TableCell>{booking.venueName}</TableCell>
          <TableCell>{booking.bookingStatus}</TableCell>
          <TableCell>{AppendDollarToAmount(booking.totalAmountInDollar)}</TableCell>
          <TableCell>{AppendDollarToAmount(booking.averageAmountInDollar)}</TableCell>
          <TableCell>{AppendDollarToAmount(booking.averageAmountSpentOnFoodInDollar)}</TableCell>
          <TableCell>{AppendDollarToAmount(booking.averageAmountSpentOnBeverageInDollar)}</TableCell>
          <TableCell>{booking.bookingDuration && `${booking.bookingDuration} minutes`}</TableCell>
          <TableCell>{booking.bookingTime && Moment(booking.bookingTime).format('DD MMM YYYY hh:mm A')}</TableCell>
          <TableCell>{booking.tableList}</TableCell>
          <TableCell>
          {
            booking.tags.split(',').map((tag,index) => {
              return (
                <span key={index}>
                  {tag === "VIP" && <span className={classes.darkBlueColour}>&#9733;</span>}
                  {tag === "Previous No Show" && <span className={classes.crimsonColour}>&nbsp;&#10006;</span>}
                  {tag !== "VIP" && tag !== "Previous No Show" && <span>&nbsp;{tag}</span>}
                </span>
              )
            })
          }
          </TableCell>
          <TableCell>
          {
            booking.customerVisitCount > 1 &&
            <>
              <span className={classes.darkGreenColour}>{booking.customerVisitCount}</span>
              <span className={classes.darkGreenColour}>&nbsp;&#x27F3;</span>
            </>
          }
          </TableCell>
        </TableRow>
        <TableRow>
          <PurchasedItemsTable isOpen={open} purchasedItemList={itemList} />
        </TableRow>
      </>
    );
  };

  return (
    <Paper>
      <Grid container className={classes.buttonGroupRoot}>
        <TextField
          variant="outlined"
          value={filterParameters.searchText}
          placeholder="Search.."
          className={classes.searchTextField}
          onChange={(e) => dispatch(saveSearchText(e.target.value))}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchOutlinedIcon /></InputAdornment>,
            endAdornment: !!filterParameters.searchText ? (
              <IconButton onClick={() => dispatch(saveSearchText(""))}>
                <CancelRoundedIcon />
              </IconButton>
            ): null
          }}
        />
        <div>
          <Button 
            variant="contained" 
            className={classes.nextPrevWeekButton} 
            color="primary"
            onClick={() => onNextPrevWeekButtonClickHandler(-1)}
          ><ArrowBackIos /> 1 Week
          </Button>
          <Button 
            variant="contained" 
            className={classes.nextPrevWeekButton} 
            color="primary"
            onClick={() => onNextPrevWeekButtonClickHandler(1)}
          >1 Week<ArrowForwardIos />
          </Button>
        </div>
      </Grid>
      <TableContainer className={classes.root}>
        <Table stickyHeader aria-label="sticky collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              {
                headerCells.map(header => (
                  <TableCell key={header.id} sortDirection={orderBy === header.id ? order : false}>
                    <TableSortLabel
                      style={{minWidth: header.minWidth || 60}}
                      active={orderBy === header.id}
                      direction={orderBy === header.id ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, header.id)}>
                      {header.label}
                      {
                        orderBy === header.id ? (
                          <span className={classes.visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </span>
                        ) : null
                      }
                    </TableSortLabel>
                  </TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((booking, index) => <Row key={index} {...booking} />)
            }
          </TableBody>
        </Table>
      </TableContainer>
      {
        rows.length > 0 &&
        <TablePagination
          page={page}
          component="div"
          labelRowsPerPage=""
          count={rows.length}
          rowsPerPageOptions={[]}
          rowsPerPage={rowsPerPage}
          onChangePage={(event: unknown, newPage: number) => setPage(newPage)} />
      }
    </Paper>
  )
}
